import { BankOutlined } from '@ant-design/icons';
import { Avatar, Button, Col, Modal, Row, Select, TableColumnsType, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import {
	AiOutlineAim,
	AiOutlineArrowDown,
	AiOutlineCopy,
	AiOutlineEye,
	AiOutlineEyeInvisible,
	AiOutlineFileText,
	AiOutlineLike,
	AiOutlinePlus,
	AiOutlineStop
} from 'react-icons/ai';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Subscription } from 'rxjs';
import { ButtonType, TAButton, TASearchInput } from '../../../components';
import ActionsMenu from '../../../components/base/data-display/actions-menu';
import DataDisplay from '../../../components/base/data-display/data-display-selector';
import { ComponentGuard } from '../../../components/component-guard';
import { MatchModalFormType } from '../../../components/dashboard/forms/match-modal-form';
import { RecommendCandidateModal } from '../../../components/dashboard/recommend-candidate-modal';
import { ApplicantsStateCount, SectionTitle, TASelect, Title, TitleLink } from '../../../components/dashboard/styles';
import { ApplicantStates } from '../../../constants/applicant-states';
import { JobStatus, jobStatusIconMap, JobStatusMap } from '../../../constants/job-status';
import { Role } from '../../../enums';
import { useMedia, usePrevious } from '../../../hooks';
import authService from '../../../services/auth.service';
import companyService from '../../../services/company.service';
import jobsService from '../../../services/jobs.service';
import { IMenuAction } from '../../../types';
import { appendQueryParam } from '../../../utils/append-query-param';
import configService from '../../../utils/config';
import { extractSkillsFromDescription } from '../../../utils/extract-skills';
import { notifyError, notifySuccess } from '../../../utils/notification';
import { objToSearchFilter } from '../../../utils/obj-to-search-filter';
import { objectToSearchParams } from '../../../utils/object-to-searchparams';
import { cleanUndefined } from '../../../utils/objects';
import { parseParams } from '../../../utils/parse-params';
import { verificationMapper } from '../../../utils/verification-mapper';
import classes from './jobs-dashboard.module.css';
import { useTheme } from 'styled-components';

const { Option } = Select;

const jobsItemFactory =
	backoffice =>
	({ item }) => {
		const theme = useTheme();
		return (
			<div className={classes.Container}>
				<div className={classes.Row1}>
					<TitleLink textAlign="center" to={`/dashboard/job/${item._id}`} color="#3F536E" size={15}>
						{item.title}
					</TitleLink>
				</div>
				<div className={classes.Row1}>posted {dayjs(item.createdAt).format('ddd, MMM D, YYYY h:mm A')}</div>
				<div className={classes.Row1}>
					<Tag color={JobStatusMap[item.status]}>{item.status}</Tag>
				</div>
				{backoffice ? (
					<div className={classes.Row1}>
						<Link to={`/company/${item.company.id}`} style={{ color: theme.TA_GREEN }}>
							<span className="d-flex align-items-center">
								<span className="me-1">{item.company.name}</span>
								{verificationMapper(theme)[item.company.joinStatus]}
							</span>
						</Link>
					</div>
				) : null}

				<div className={classes.Row1}>
					{Object.keys(item).map(state => {
						return ApplicantStates[state] ? (
							<ApplicantsStateCount count={item[state]} name={ApplicantStates[state]} size="mobile" />
						) : null;
					})}
				</div>
			</div>
		);
	};

export const JobsDashboard: React.FC<any> = ({ backOffice }) => {
	const [jobs, setJobs] = useState<any>([]);
	const [mobileDisplay, setMobileDisplay] = useState<any>([]);
	const [modalVisible, setModalVisible] = useState<any>([]);
	const [companies, setCompanies] = useState<any>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedCompany, setSelectedCompany] = useState<any>(null);
	const [selectedTitle, setSelectedTitle] = useState<any>(null);
	const [selectedStatus, setSelectedStatus] = useState<any>(null);
	const [jobToRecommend, setJobToRecommend] = useState<any>(null);
	const [allPosts, setAllPosts] = useState<boolean>(true);
	const [displayWatchedJobs, setDisplayWatchedJobs] = useState<boolean>(false);
	const [pagination, setPagination] = useState<any>({});
	const [jobsFilter, setJobsFilter] = useState<any>({});
	const [companiesFilter, setCompaniesFilter] = useState<any>({});
	const [sortParams, setSortParams] = useState<any>({});
	const mediaView = useMedia('md');
	const location = useLocation();
	const prevLocation = usePrevious(location);
	const navigate = useNavigate();
	const theme = useTheme();

	const tableColumns: TableColumnsType<any> = [
		{
			title: (
				<span className="d-inline-block ps-3 py-2" style={{ color: '#8DABC4' }}>
					Title
				</span>
			),
			dataIndex: 'title',
			key: 'title',
			render: (text, record) => (
				<TitleLink
					to={`/dashboard/job/${record._id}`}
					color="#3F536E"
					size={15}
					className="ps-3 py-2 d-inline-block"
					style={{
						maxWidth: '300px',
						whiteSpace: 'break-spaces',
						wordBreak: 'break-word'
					}}
				>
					{text}
				</TitleLink>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Status
				</span>
			),
			dataIndex: 'status',
			key: 'status',
			render: status => <Tag color={JobStatusMap[status]}>{status}</Tag>
			// sorter: jobsSorter,
		},

		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Posted At
				</span>
			),
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (text, record) => <span>{dayjs(text).format('ddd, MMM D, YYYY h:mm A')}</span>
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Interested
				</span>
			),
			dataIndex: 'interested',
			key: 'interested',
			render: (text, record) => (
				<Link
					to={`/dashboard/job-board?job=${record._id}&state=int&companyId=${record.company.id}&companyName=${record.company.name}`}
					style={{ color: theme.TA_GREEN }}
				>
					{text ?? 0}
				</Link>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					To Interview
				</span>
			),
			dataIndex: 'toInterview',
			key: 'toInterview',
			render: (text, record) => (
				<Link
					to={`/dashboard/job-board?job=${record._id}&state=tinv&companyId=${record.company.id}&companyName=${record.company.name}`}
					style={{ color: theme.TA_GREEN }}
				>
					{text ?? 0}
				</Link>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Offer
				</span>
			),
			dataIndex: 'offer',
			key: 'offer',
			render: (text, record) => (
				<Link
					to={`/dashboard/job-board?job=${record._id}&state=off&companyId=${record.company.id}&companyName=${record.company.name}`}
					style={{ color: theme.TA_GREEN }}
				>
					{text ?? 0}
				</Link>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Hired
				</span>
			),
			dataIndex: 'hired',
			key: 'hired',
			render: (text, record) => (
				<Link
					to={`/dashboard/job-board?job=${record._id}&state=hir&companyId=${record.company.id}&companyName=${record.company.name}`}
					style={{ color: theme.TA_GREEN }}
				>
					{text ?? 0}
				</Link>
			),
			sorter: { multiple: 1 }
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Rejected
				</span>
			),
			dataIndex: 'rejected',
			key: 'rejected',
			render: (text, record) => (
				<Link
					to={`/dashboard/job-board?job=${record.id}&state=rej&companyId=${record.company.id}&companyName=${record.company.name}`}
					style={{ color: theme.TA_GREEN }}
				>
					{text ?? 0}
				</Link>
			),
			sorter: { multiple: 1 }
		},

		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Actions
				</span>
			),
			dataIndex: 'actions',
			key: 'actions',
			render: (text, record) => <ActionsMenu dotsOrientation="horizontal" options={actionsFactory(record)} />
		}
	];

	if (backOffice) {
		tableColumns.splice(0, 0, {
			title: (
				<span className="d-inline-block ps-3 py-2" style={{ color: '#8DABC4' }}>
					Code
				</span>
			),
			dataIndex: 'code',
			key: 'code',
			width: '8%',
			render: (text, record) => (
				<TitleLink
					to={`/dashboard/job/${record._id}`}
					color="#3F536E"
					size={15}
					className="ps-3 py-2 d-inline-block"
					style={{
						maxWidth: '300px',
						whiteSpace: 'break-spaces',
						wordBreak: 'break-word'
					}}
				>
					{text}
				</TitleLink>
			)
		});
		tableColumns.splice(2, 0, {
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Company
				</span>
			),
			dataIndex: ['company', 'name'],
			key: 'company.name',
			render: (name, record) => {
				const companyName = (
					<>
						<span className="mx-2">{name}</span>
						{verificationMapper(theme)[record.company.joinStatus]}
					</>
				);
				return (
					<Link
						to={`/dashboard/company/${record.company.id}`}
						style={{ color: theme.TA_GREEN }}
						className="d-flex"
					>
						{companyName}
					</Link>
				);
			}
		});
	}

	const fetchJobs = async (params, watched?: boolean) => {
		const resp = await (watched === true ? jobsService.getWatchedJobsSummary(params) : jobsService.getJobs(params));
		return resp;
	};

	const fetchCompanies = async params => {
		const resp = await companyService.getCompanies(params);
		return resp;
	};

	const searchObs$ = useMemo(() => {
		return companyService.getPaginatedListFetcher({
			fetcher: v => {
				return fetchJobs(
					{
						filter: v && v['filter'] ? objToSearchFilter(v['filter']) : null,
						pageSize: configService.defaultPageSize,
						sorters: sortParams,
						page: v && v['page'] ? v['page'] : null
					},
					v['watched']
				);
			},
			debounce: 700,
			distinct: true,
			beforeFetch: v => setJobsFilter(v && v['filter'] ? v['filter'] : {})
		});
	}, []);

	const searchCompanies$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: v =>
					fetchCompanies({
						filter: v && v['filter'] ? objToSearchFilter(v['filter']) : null,
						pageSize: configService.defaultPageSize
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: v => setCompaniesFilter(v && v['filter'] ? v['filter'] : {})
			}),
		[]
	);

	const changePage$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: fetchJobs,
				debounce: false,
				distinct: false,
				beforeFetch: v => setLoading(true)
			}),
		[]
	);

	const sortObs$ = useMemo(
		() =>
			companyService.getPaginatedListFetcher({
				fetcher: ({ filter, sortInfo }) =>
					fetchJobs({
						sorters: sortInfo,
						pageSize: configService.defaultPageSize,
						filter
					}),
				debounce: 700,
				distinct: true,
				beforeFetch: ({ sortInfo }) => {
					setSortParams(sortInfo ? { ...sortInfo } : undefined);
					setLoading(true);
					setJobs([]);
				}
			}),
		[]
	);

	const subsribtions = new Subscription();

	const fetchQueryCompany = async (filter, companies) => {
		if (backOffice) {
			if (filter.companyId) {
				let company = companies.find(company => company.id === filter.companyId);
				if (!company) {
					companies.push(
						(
							await companyService.getCompanies({
								filter: objToSearchFilter({ _id: filter.companyId })
							})
						).data[0]
					);
				}
			}
		}
	};

	const setCVViewable = async (item, cvViewable) => {
		let job;
		try {
			job = await jobsService.jobCVViewable(item._id, cvViewable);
			if (!job) throw new Error('Something went wrong');
			setJobs(
				jobs.map(j => {
					if (j.id === item.id) {
						j.cvViewable = cvViewable;
						if (cvViewable === false) j.cvDownloadable = false;
					}
					return j;
				})
			);

			setMobileDisplay([
				mobileDisplay[0].map(j => {
					if (j.id === item.id) {
						j.cvViewable = cvViewable;
						if (cvViewable === false) j.cvDownloadable = false;
					}
					return j;
				})
			]);

			notifySuccess('Updated Successfully');
		} catch (e: any) {
			notifyError(e.message);
		}
	};
	const setCVDownloadable = async (item, cvDownloadable) => {
		let job;
		try {
			job = await jobsService.jobCVDownloadable(item._id, cvDownloadable);
			if (!job) throw new Error('Something went wrong');
			setJobs(
				jobs.map(j => {
					if (j.id === item.id) j.cvDownloadable = cvDownloadable;
					return j;
				})
			);
			setMobileDisplay([
				mobileDisplay[0].map(j => {
					if (j.id === item.id) j.cvDownloadable = cvDownloadable;
					return j;
				})
			]);
			notifySuccess('Updated Successfully');
		} catch (e: any) {
			notifyError(e.message);
		}
	};
	const mountLogic = async () => {
		if (backOffice)
			tableColumns.splice(
				1,
				1,
				{
					title: (
						<span className="d-inline-block ps-3 py-2" style={{ color: '#8DABC4' }}>
							Title
						</span>
					),
					dataIndex: 'title',
					key: 'title',
					render: (text, record) => (
						<TitleLink
							to={`/dashboard/job/${record._id}`}
							color="#3F536E"
							size={15}
							className="ps-xl-3 py-2 d-flex flex-column flex-xl-row"
						>
							<Avatar
								size={38}
								src={companies.find(c => c.id === record.company.id)?.logo || null}
								icon={<BankOutlined />}
								shape="square"
							/>
							<span className="ms-xl-2">{text}</span>
						</TitleLink>
					),
					sorter: { multiple: 1 }
				},
				{
					title: (
						<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
							Company
						</span>
					),
					dataIndex: ['company', 'name'],
					key: 'company.name',
					render: (name, record) => {
						const companyName = (
							<>
								<span className="mx-2">{name}</span>
								{verificationMapper(theme)[record.company.joinStatus]}
							</>
						);
						return (
							<Link
								to={`/dashboard/company/${record.company.id}`}
								style={{ color: theme.TA_GREEN }}
								className="d-flex"
							>
								{companyName}
							</Link>
						);
					}
				}
			);
		subsribtions.add(
			searchObs$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setJobs(data);
				setMobileDisplay([data]);
				setPagination(pagination);
			})
		);
		if (backOffice) {
			subsribtions.add(
				searchCompanies$.subscribe(({ data: companies }) => {
					setLoading(false);
					setCompanies(companies);
				})
			);
		}

		subsribtions.add(
			changePage$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setJobs(data);
				setMobileDisplay(prevState => [...prevState, data]);
				setPagination(pagination);
			})
		);

		subsribtions.add(
			sortObs$.subscribe(({ data, pagination }) => {
				setLoading(false);
				setJobs(data);
				setMobileDisplay([data]);
				setPagination(pagination);
			})
		);

		let qs: any = {};
		if (location && location.search) {
			qs = parseParams(location.search);
			qs.page = +qs.page;
			qs.pageSize = +qs.pageSize;
		}
		if (!(qs.status || qs.title || qs.companyId || qs.page || qs.watched))
			navigate({
				search: objectToSearchParams({ page: 1, pageSize: configService.defaultPageSize, watched: false })
			});

		let pagination, companies, companiesPayload, jobs;
		try {
			setLoading(true);
			[{ data: jobs, pagination }, companiesPayload] = await Promise.all([
				fetchJobs({
					page: qs.page ? qs.page : 1,
					pageSize: qs.pageSize ? qs.pageSize : configService.defaultPageSize
				}),
				backOffice ? companyService.getCompanies() : Promise.resolve(undefined)
			]);
			companies = companiesPayload?.data;
			fetchQueryCompany(qs, companies);

			//handle if no jobs are sent.
			if (jobs === null || (backOffice && companies === null))
				throw new Error('Error while fetching data, Please try again later.');
		} catch (e: any) {
			//handle promise error.
			notifyError(e.message);
			setLoading(false);
			return;
		}

		setJobs(jobs);
		setMobileDisplay([jobs]);
		setPagination(pagination);
		setCompanies(backOffice ? companies : undefined);
		setLoading(false);
		setDisplayWatchedJobs(qs.watched === 'true');

		if (location && location.search) {
			const queryParams = {};
			if (backOffice && qs.companyId) {
				queryParams['companyId'] = qs.companyId;
				setSelectedCompany(qs.companyId);
			}

			if (qs.status) {
				queryParams['status'] = qs.status;
				setSelectedStatus(qs.status);
			}
			if (qs.title) {
				queryParams['title'] = qs.title;
				setSelectedTitle(qs.title);
			}
			const page = qs.page || 1;
			const pageSize = qs.pageSize || 10;

			if (Object.keys(queryParams).length) {
				searchObs$.next({ filter: queryParams, page, pageSize });
				navigate(objectToSearchParams({ ...queryParams, watched: qs.watched, page, pageSize }));
			}
		}
	};

	useEffect(() => {
		mountLogic();
		return () => subsribtions.unsubscribe();
	}, []);

	const locationSearchChangeLogic = () => {
		const newSearch = location?.search;
		const oldSearch = prevLocation?.search;
		if (!newSearch) {
			changePage$.next();
			setJobsFilter({});
			setCompaniesFilter({});
			setSelectedCompany(null);
		} else if (newSearch && oldSearch && newSearch !== oldSearch) {
			const newParams = parseParams(newSearch);
			const oldParams = parseParams(oldSearch);

			const newFilter: any = cleanUndefined({
				companyId: newParams.companyId,
				status: newParams.status,
				title: newParams.title
			});
			const oldFilter: any = cleanUndefined({
				companyId: oldParams.companyId,
				status: oldParams.status,
				title: oldParams.title
			});

			// if (!newFilter.companyId)
			// 	setState({
			// 		companiesFilter: {},
			// 		selectedCompany: null,
			// 		jobsFilter: { ...jobsFilter, companyId: undefined }
			// 	});

			if (Object.keys(newFilter).length) {
				if (
					newFilter.companyId !== oldFilter.companyId ||
					newFilter.status !== oldFilter.status ||
					newFilter.title !== oldFilter.title
				) {
					fetchQueryCompany(newFilter, companies);
					setJobsFilter(newFilter);
					setSelectedCompany(newFilter.companyId);
					searchObs$.next({ filter: newFilter });
				}
			}
			if (
				(newParams.page && newParams.page !== oldParams.page) ||
				(newParams.pageSize && newParams.pageSize !== oldParams.pageSize)
			) {
				if (!mediaView) searchObs$.next({ filter: newFilter });
				else
					changePage$.next({
						page: newParams.page,
						pageSize: newParams.pageSize,
						filter: objToSearchFilter(newFilter),
						sorters: sortParams
					});
			}
		}
	};

	useEffect(() => {
		if (location?.search) locationSearchChangeLogic();
	}, [location?.search]);

	const actionsFactory = item => {
		const actions: IMenuAction[] = JobStatus.filter(s => s !== item.status).map(s => ({
			name: s,
			key: s,
			action: setJobStatus(item._id, s),
			icon: jobStatusIconMap[s]
		}));
		if (backOffice) {
			const companyNotVerified = !item.company.verified,
				jobNotOpen = item.status !== 'Open',
				disabled = companyNotVerified || jobNotOpen;

			actions.push(
				{
					name: 'Recommend Candidate',
					key: 'recommendCandidate',
					icon: <AiOutlineLike />,
					action: !disabled ? recommendCandidateForJob(item._id) : null,
					disabled,
					tooltipText: disabled
						? companyNotVerified
							? "Can't recommend candidates to an unverified company"
							: "Can't recommend candidates to a non open jobs"
						: ''
				},
				{
					name: !item.isWatched ? 'Watch' : 'Unwatch',
					key: 'watchJob',
					icon: !item.isWatched ? <AiOutlineEye /> : <AiOutlineEyeInvisible />,
					action: !item.isWatched ? watchJob(item) : unwatchJob(item),
					tooltipText: !item.isWatched
						? 'Watch to get notified for changes'
						: 'Unwatch to not get notified for changes'
				},
				{
					name: `${item.cvViewable ? 'Disallow' : 'Allow'} CV View`,
					key: 'cvViewable',
					icon: item.cvViewable ? <AiOutlineEyeInvisible /> : <AiOutlineFileText />,

					action: () => setCVViewable(item, item.cvViewable ? false : true)
				},
				{
					name: `${item.cvDownloadable ? 'Disallow' : 'Allow'} CV Download`,
					key: 'cvDownloadable',
					icon: item.cvDownloadable ? <AiOutlineStop /> : <AiOutlineArrowDown />,
					disabled: !item.cvViewable,

					action: () => setCVDownloadable(item, item.cvDownloadable ? false : true)
				},
				{
					name: 'Match for Candidates',
					key: 'matchForCandidates',
					icon: <AiOutlineAim />,
					tags: [
						{
							text: 'BETA',
							bgColor: '#f50',
							textColor: '#fff'
						}
					],
					action: () => matchForCandidates(item)
				}
			);
		} else {
			actions.push({
				name: 'Clone',
				key: 'clone',
				action: cloneJob(item._id),
				icon: <AiOutlineCopy />
			});
		}
		return actions;
	};

	const setJobStatus = (jobId, status) => () => {
		Modal.confirm({
			title: "Are you sure you want to change this job's status",
			onOk: async close => {
				try {
					let resp = await jobsService.setStatus(jobId, status);
					// if (!resp) throw new Error('Something went wrong');
				} catch (e: any) {
					console.error(e);
					notifyError(e.message);
					return;
				}
				notifySuccess(`Job status changed to ${status} successfully!`);
				setJobs(
					jobs.map(job => {
						if (job._id === jobId) {
							job.status = status;
							if (status === 'Open') job.cvViewable = true;
							else {
								job.cvViewable = false;
								job.cvDownloadable = false;
							}
						}
						return job;
					})
				);

				close();
			},
			onCancel: close => close()
		});
	};

	const recommendCandidateForJob = id => async () => {
		if (!jobs) return;
		const job = jobs.filter(job => job._id === id)[0];
		setJobToRecommend(job);
		setModalVisible(true);
	};

	const watchJob = job => async () => {
		if (!jobs) return;
		const success = await jobsService.watchJob(job.id);
		if (!success) {
			return notifyError('Something went wrong');
		}
		setJobs(
			jobs.map(otherJob => {
				if (otherJob.id === job.id) {
					otherJob.isWatched = true;
				}
				return otherJob;
			})
		);
		notifySuccess('Job added to watch list');
	};

	const unwatchJob = job => async () => {
		if (!jobs) return;
		const success = await jobsService.unwatchJob(job.id);
		if (!success) {
			return notifyError('Something went wrong');
		}
		setJobs(
			displayWatchedJobs
				? jobs.filter(otherJob => otherJob.id !== job.id)
				: jobs.map(otherJob => {
						if (otherJob.id === job.id) {
							otherJob.isWatched = false;
						}
						return otherJob;
				  })
		);
		notifySuccess('Job removed from watch list');
	};

	const onRecommendError = candidate => {
		if (!candidate) {
			console.error('Error while fetching data, Please try again later.');
			notifyError('Error while fetching data, Please try again later.');
		} else {
			notifyError(
				`Candidate ${candidate.firstName} ${candidate.lastName} is already recommended before for this job`
			);
		}
		return;
	};

	const onRecommendSuccess = candidate => {
		notifySuccess(
			`Candidate ${candidate.firstName} ${candidate.lastName} is successfully recommended to job ${jobToRecommend.title} for ${jobToRecommend.company.name} company`
		);

		if (!candidate.isRegistered)
			setJobs(
				jobs.map(job => {
					if (job._id === jobToRecommend.id) {
						job.interested++;
					}
					return job;
				})
			);
	};

	const handlePageChange = (page, pageSize) => {
		if (mediaView) {
			navigate({
				search: objectToSearchParams({
					page,
					pageSize,
					...jobsFilter,
					watched: displayWatchedJobs
				})
			});
		} else {
			changePage$.next({
				page,
				pageSize,
				filter: objToSearchFilter({ ...jobsFilter }),
				sorters: sortParams
			});
		}
	};

	const handleJobSearch = (e?: any, key?: string, watched?: boolean) => {
		let value;
		if (e && e.target && e.target.value !== undefined) value = e.target.value;
		else value = e;
		if (value === '') value = undefined;
		const filter = { ...jobsFilter };
		if (key) filter[key] = value;
		setSelectedTitle(filter['title']);
		navigate(objectToSearchParams({ ...filter, watched: displayWatchedJobs }));
		searchObs$.next({ filter, watched });
	};

	const handleCompanySearch = value => {
		searchCompanies$.next({ filter: { name: value } });
	};

	const handleCompanySelect = val => {
		const filter = val ? { companyId: val } : {};
		handleJobSearch(filter.companyId, 'companyId');
		setSelectedCompany(val);
		setLoading(false);
	};

	const handleStatusSelect = val => {
		const filter = val ? { status: val } : {};
		handleJobSearch(filter.status, 'status');
		setSelectedStatus(filter.status);
		setLoading(false);
	};

	const handleSort = sortInfo => {
		sortObs$.next({ sortInfo, filter: objToSearchFilter({ ...jobsFilter, ...companiesFilter }) });
	};

	const cloneJob = jobId => () => {
		Modal.confirm({
			title: 'You are going to clone this job.',
			onOk: async () => {
				try {
					var resp = await jobsService.cloneJob(jobId);
					if (!resp) throw new Error('Something went wrong');
				} catch (e: any) {
					notifyError(e.message);
					console.error(e);
					return;
				}
				const allJobs = [...jobs, resp];
				setJobs(allJobs);
			}
		});
	};

	const matchForCandidates = async item => {
		const detailedJob = await jobsService.getJob(item._id);
		const jobSkills = detailedJob.skills && detailedJob.skills.length ? detailedJob.skills.map(s => s.name) : [];
		const extractedSkills = detailedJob.jobDescription
			? extractSkillsFromDescription(detailedJob.jobDescription)
			: [];
		const skills = Array.from(new Set([...jobSkills, ...extractedSkills]));
		const values = cleanUndefined({
			title: detailedJob.title ?? undefined,
			careerLevel: detailedJob.careerLevel ?? undefined,
			skills: skills && skills.length ? skills : undefined,
			minExp: detailedJob.yearsOfExp.min ?? undefined,
			maxExp: detailedJob.yearsOfExp.max ?? undefined,
			type: MatchModalFormType.jtoc,
			keywords: detailedJob.keywords && detailedJob.keywords.length ? detailedJob.keywords : undefined,
			// description: detailedJob.jobDescription,
			maxSalary: detailedJob.package.max ?? undefined
		});
		navigate({
			pathname: configService.URIs.matching_results_uri,
			search: objectToSearchParams(values)
		});
	};

	const handleDisplayWatched = display => {
		setLoading(true);
		setDisplayWatchedJobs(display);
		handleJobSearch(undefined, undefined, display);
		appendQueryParam(location.search, navigate, [{ watched: display }, { page: 1 }]);
	};

	return (
		<div className={`dashboard`} style={{ width: '95%' }}>
			<Row className="text-start page-label">
				<SectionTitle>Jobs</SectionTitle>
			</Row>
			<Row gutter={8} className="justify-content-between mx-auto mb-3">
				<Col xs={24} lg={backOffice ? 6 : 14} className="text-start d-flex align-items-end mb-3 mb-lg-0">
					You have{' '}
					<span
						style={{
							color: theme.TA_GREEN,
							borderBottom: `2px dashed ${theme.TA_GREEN}`,
							fontSize: '16px',
							marginLeft: '5px'
						}}
					>
						{pagination.total} jobs
					</span>
				</Col>
				{backOffice ? (
					<>
						<Col
							xs={24}
							lg={{ span: 5, pull: 3 }}
							className="d-flex justify-content-center justify-content-lg-end"
						>
							<Button.Group style={{ padding: '4px 0px', height: 'auto' }}>
								<TAButton
									btntype={!displayWatchedJobs ? ButtonType.PRIMARY : ButtonType.GHOST}
									onClick={() => handleDisplayWatched(false)}
								>
									All Jobs
								</TAButton>
								<TAButton
									btntype={displayWatchedJobs ? ButtonType.PRIMARY : ButtonType.GHOST}
									onClick={() => handleDisplayWatched(true)}
								>
									My Watchlist
								</TAButton>
							</Button.Group>
						</Col>
						<Col xs={24} lg={{ span: 5 }}>
							<TASelect
								value={selectedCompany || undefined}
								onChange={handleCompanySelect}
								filterOption={(input, option) =>
									option.props.children[0].props.children
										.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0
								}
								placeholder="Select a company"
								onSearch={handleCompanySearch}
								className="mb-2 mb-lg-0"
								showSearch
							>
								{companies.map(c => (
									<Select.Option value={c.id} key={c.id}>
										<span className="mx-2">{c.name}</span>
										{verificationMapper(theme)[c.joinStatus]}
									</Select.Option>
								))}
							</TASelect>
						</Col>
					</>
				) : null}
				<Col xs={24} lg={backOffice ? { span: 5 } : { span: 4 }}>
					<TASearchInput
						value={selectedTitle || undefined}
						onChange={e => handleJobSearch(e, 'title')}
						placeholder="Search Jobs"
						width="100%"
						className="mb-2 mb-lg-0"
					/>
				</Col>
				<Col xs={24} lg={backOffice ? { span: 3 } : { span: 3 }}>
					<TASelect
						value={selectedStatus || undefined}
						showSearch={false}
						onChange={handleStatusSelect}
						placeholder="status"
					>
						{JobStatus.map(status => (
							<Option value={status} key={status}>
								<Tag color={JobStatusMap[status]}>{status}</Tag>
							</Option>
						))}
					</TASelect>
				</Col>
				{!backOffice ? (
					<Col xs={24} lg={{ span: 3 }} className="d-flex justify-content-center d-lg-block mt-2 mt-lg-0">
						<ComponentGuard
							allowed={authService.hasRoles(Role.COMPANY_OWNER, Role.COMPANY_ADMIN)}
							fallback={undefined}
						>
							<TAButton
								btntype={ButtonType.PRIMARY}
								style={{ padding: '11px 20px', height: 'auto' }}
								onClick={() => navigate('/dashboard/add-job')}
							>
								<Title icon={<AiOutlinePlus size={20} />} button>
									Add Job
								</Title>
							</TAButton>
						</ComponentGuard>
					</Col>
				) : null}
			</Row>
			<div className="dashboard-content h-auto">
				<DataDisplay
					data={jobs}
					mobileData={mobileDisplay}
					loading={loading}
					tableColumns={tableColumns}
					listItemComponent={jobsItemFactory(backOffice)}
					renderActions={actionsFactory}
					pagination={{ position: 'bottom', ...pagination }}
					onLoadMore={handlePageChange}
					onSort={handleSort}
					className="ta-table"
				/>
				{backOffice && jobToRecommend ? (
					<RecommendCandidateModal
						visible={modalVisible}
						job={jobToRecommend}
						company={jobToRecommend.company}
						onSuccess={onRecommendSuccess}
						onError={onRecommendError}
						onCancel={() => {
							setModalVisible(false);
							setJobToRecommend(null);
						}}
					/>
				) : null}
			</div>
		</div>
	);
};
